import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReferralWorkspaceView } from '@common/api/featureService';

export interface ReferralViewApi {
    getWorkspaceReferral(id: string): Promise<ReferralWorkspaceView>;
    createWorkspaceReferralCode(id: string, key: string): Promise<ReferralWorkspaceView>;
    rotateWorkspaceReferralCode(id: string, key: string): Promise<ReferralWorkspaceView>;
}

export function useReferralView(workspaceId: string | undefined, enabled: boolean, api: ReferralViewApi, loadError: string, actionError: string) {
    const scope = enabled ? workspaceId : undefined;
    const currentScope = useRef(scope);
    const scopeCycle = useRef(0);
    const generation = useRef(0);
    const mounted = useRef(false);
    if (currentScope.current !== scope) {
        currentScope.current = scope;
        scopeCycle.current++;
        generation.current++;
    }
    const [state, setState] = useState<{ scope?: string; cycle: number; view: ReferralWorkspaceView | null; loading: boolean; error: string | null; action: 'create' | 'rotate' | null }>({ cycle: scopeCycle.current, view: null, loading: false, error: null, action: null });
    const load = useCallback(async ({ reportRefreshError = false }: { reportRefreshError?: boolean } = {}) => {
        const cycle = scopeCycle.current;
        const request = ++generation.current;
        if (!scope) {
            setState({ cycle, view: null, loading: false, error: null, action: null });
            return;
        }
        setState(previous => ({
            scope,
            cycle,
            view: previous.scope === scope && previous.cycle === cycle ? previous.view : null,
            loading: true,
            error: null,
            action: null,
        }));
        try {
            const view = await api.getWorkspaceReferral(scope);
            if (generation.current === request) setState({ scope, cycle, view, loading: false, error: null, action: null });
        } catch (error: any) {
            if (generation.current === request) {
                setState(previous => ({
                    scope,
                    cycle,
                    view: previous.scope === scope && previous.cycle === cycle ? previous.view : null,
                    loading: false,
                    error: !reportRefreshError && previous.scope === scope && previous.cycle === cycle && previous.view
                        ? null
                        : error?.message || loadError,
                    action: null,
                }));
            }
        }
    }, [scope, api, loadError]);
    useEffect(() => {
        mounted.current = true;
        void load();
        return () => { mounted.current = false; generation.current++; };
    }, [load]);
    const actionLocks = useRef(new Set<string>());
    const actionKeys = useRef(new Map<string, string>());
    const act = useCallback(async (action: 'create' | 'rotate') => {
        if (!scope || actionLocks.current.has(scope)) return;
        actionLocks.current.add(scope);
        const cycle = scopeCycle.current;
        const request = ++generation.current;
        setState(previous => ({ ...previous, scope, cycle, action, error: null }));
        try {
            const keyScope = `${scope}:${action}`;
            const key = actionKeys.current.get(keyScope) || `referral-${action}-${crypto.randomUUID()}`;
            actionKeys.current.set(keyScope, key);
            const view = action === 'create' ? await api.createWorkspaceReferralCode(scope, key) : await api.rotateWorkspaceReferralCode(scope, key);
            actionKeys.current.delete(keyScope);
            if (generation.current === request) setState({ scope, cycle, view, loading: false, error: null, action: null });
            else if (mounted.current && currentScope.current === scope) await load();
        } catch (error: any) {
            if (generation.current === request) setState(previous => ({ ...previous, loading: false, action: null, error: error?.message || actionError }));
            else if (mounted.current && currentScope.current === scope) await load();
        } finally {
            actionLocks.current.delete(scope);
        }
    }, [scope, api, actionError, load]);
    const stateMatchesScopeCycle = state.scope === scope && state.cycle === scopeCycle.current;
    return {
        view: stateMatchesScopeCycle ? state.view : null,
        loading: Boolean(scope) && (!stateMatchesScopeCycle || state.loading),
        error: stateMatchesScopeCycle ? state.error : null,
        action: stateMatchesScopeCycle ? state.action : null,
        load,
        act,
    };
}
